import React from 'react'

const tmb = {
    id: 'hiking-tour-du-mont-blanc',
    type: 'post',
    countryId: 'westernEurope',
    title: 'Hiking the Tour du Mont Blanc',
    longDate: 'August 3, 2018',
    month: 'Aug',
    date: '3',
    coverImage: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/CoverTMB.jpg',
    content: [
        {
            text: (
                <div>One of our goals for our year(s) of travel is to spend more time hiking and generally outdoors. So, with minimal backpacking experience, we signed up for a trek which circles an Alp mountain range and takes 8-12 days. Sounds reasonable, right? We bought new backpacks, practiced in June with a couple of shorter treks in <a href="/post/lake-district/">England</a>, and <a href="/post/laugavegur/">Iceland</a>, and planned for a week of relaxing in the South of France afterward.</div>
            )
        },
        {
            text: (
                <div>There were literal highs and lows on this trip — every day seemed to take us up and over a mountain, then back down to a village for the night — as well as emotional highs and lows. I’ve given each day a grade, which is not grading the quality of the hike that day so much as our performance as travelers taking on what the day threw at us. Quick note - I make a few references to Kev Reynolds’s “Trekking the Tour of Mont Blanc” book, which is essential.</div>
            )
        },
        {
            text: (
                <div>If you’re interested in doing the hike, please <a href="mailto:alicecamilleavery@gmail.com">email me</a>! There were a lot of quirks in planning the trip I’d be happy to pass on.</div>
            )
        },
        {
            text: (
                <div>You can follow along with <a href="https://umap.openstreetmap.fr/fr/map/tour-du-mont-blanc-ccpmb-_206457#10/45.8710/6.9558">this map</a> of the trail. Here was our itinerary:
          <ul>
                        <li><b>Day 1:</b> Les Houches to Les Contamines, <i>16 km</i></li>
                        <li><b>Day 2:</b> Les Contamines to Les Chapieux, <i>18 km</i></li>
                        <li><b>Day 3:</b> Les Chapieux to Rifugio Combal, <i>18.5 km</i></li>
                        <li><b>Day 4:</b> Rifugio Combal to Courmayeur, <i>14.5 km</i></li>
                        <li><b>Day 5:</b> Courmayeur to Rifugio Elena, <i>19 km</i></li>
                        <li><b>Day 6:</b> Rifugio Elena to La Fouly, <i>13 km</i></li>
                        <li><b>Day 7:</b> Rest day in La Fouly</li>
                        <li><b>Day 8:</b> La Fouly to Champex, <i>15 km</i></li>
                        <li><b>Day 9:</b> Champex to Col de la Forclaz, <i>16 km</i></li>
                        <li><b>Day 10:</b> Col de la Forclaz to Tré-le-Champ, <i>18 km</i></li>
                        <li><b>Day 11:</b> Tré-le-Champ to Chamonix, <i>13 km plus cable car</i></li>
                    </ul>
                </div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/Cows.jpg',
            detailText: 'Met a lot of pretty cows along the way!'
        },
        {
            header: 'Day 1'
        },
        {
            text: (
                <div>The aspect of the Tour du Mont Blanc that makes it so charming — passing through small Alp towns — can also make navigation difficult. The path is occasionally poorly marked, intersects frequently with biking trails and offshoot paths, and key turns in and out of villages are not always signposted. Mission number one in Les Houches was to find the start of the trail, and we were not fantastic at it. We walked a couple of kilometers up and down the road before even starting the main trek, and then unfortunately got lost several more times that day. Since we had started late and it was close to 90 degrees, day 1 was pretty tough.</div>
            )
        },
        {
            text: (
                <div>After hiking up to the top of the chair lift, our lunch stop gave us a glorious view of the Bionnassay Glacier. Sadly, the lunch itself of a quiche from the supermarket was surprisingly bad. The rest of the day we struggled up hills in full sun and wandered through towns where everyone else seemed to be hiding inside.</div>
            )
        },
        {
            text: (
                <div>Our Airbnb was about 1.5 kilometers south of Les Contamines along the trail, meaning we got the first part of tomorrow’s walk done early but were exhausted by the time we finally showed up. Unfortunately, we had not thoroughly read the booking description. If we had, we would not have booked a place where “self-service” was defined as bringing your own sheets, towels, and dish towels (!) and needing to clean the apartment top to bottom in the morning. We slept in our clothes with our sweaters as pillowcases — indoor camping!</div>
            )
        },
        {
            text: (
                <div><i>Grade: B-</i></div>
            )
        },
        {
            header: 'Day 2'
        },
        {
            text: (
                <div>We woke up with very sore legs feeling scared about the day’s 18 kilometers and 1316m elevation gain. We’d learned our lesson about the heat, though, so were on the trail by 8 a.m. Pretty soon we hit the hill that we’d stay on for the entire morning, which felt like doing a Stairmaster for four hours. The book described it as “a reasonably tough crossing.” The author must be one of those ultra marathoners or else didn’t want to scare off readers, because day 2 was extremely difficult and turned out to be the hardest day (confirmed by everyone we met during the rest of the journey.)</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/day2TMB.jpg',
            detailText: 'The Stairmaster.'
        },
        {
            text: (
                <div>Day 2 also brought our first snow crossings. In late July, the snow was a dangerous combination of slushy and thin. Finally, something the Laugavegur trek in Iceland had prepared us for!</div>
            )
        },
        {
            text: (
                <div>Around 1 p.m., we made it to the crest of Col du Bonhomme, which had truly stunning views of the valleys on either side and was sheltered from the wind. I lay down in the sun and promptly fell asleep. I discovered sun naps last summer, and it’s now the only way I will nap. Highly recommend.</div>
            )
        },
        {
            text: (
                <div>We had another 45 minutes of uphill before arriving at the Refuge du Bonhomme. Our pasta carbonara arrived promptly and was absolutely massive, with an egg yolk still in its half shell balanced delicately on top. This place is at the top of a mountain that cars can’t reach, which means staff have to run down and back up it every morning to get fresh ingredients.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/pasta.jpg'
        },
        {
            text: (
                <div>The steep scramble down to Les Chapieux went quickly, and we waited for the 6 p.m. bus to Bourg Saint-Maurice while playing cards and reading. Les Chapieux is not so much as a town as three buildings: the refuge, a tiny cheese shop, and an information center. We hadn’t been able to book the refuge for the night, but the benefit of bussing out to a larger town was getting to eat seared duck and fries for dinner — delicious.</div>
            )
        },
        {
            text: (
                <div><i>Grade: B</i></div>
            )
        },
        {
            header: 'Day 3'
        },
        {
            text: (
                <div>Day 3 was another long day, with 18.5 km of walking. We couldn’t get a reservation at the recommended refuge, so like day 1, we needed to continue into the next day’s stage to get to where we would sleep.</div>
            )
        },
        {
            text: (
                <div>As we climbed the hill, we got our first glimpse of the eponymous Mont Blanc through the clouds. I snapped a photo, but none of the photos I took the entire trip have come close to depicting how grand and impressive the landscape is in person. Our energy was really starting to flag, but fresh pasta at lunch and cooler temperatures in the afternoon helped us with the final push up the hill.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/View1.jpg'
        },
        {
            text: (
                <div>The Cabane du Combal, our lodging for the night, was surrounded by a near moat of turquoise water. It turned out to be extremely nice, and I’d recommend making the final push along 5 flat kilometers to stay there. We shared a four-bed room with two retired women who were also from the Bay Area. At dinner, Carol and Jean ordered a quarter liter of wine but were given a half liter, and didn’t finish it. We’d thought before the trip we’d try not to drink but since it was free… a small glass doesn’t count, right?</div>
            )
        },
        {
            text: (
                <div><i>Grade: A</i></div>
            )
        },
        {
            header: 'Day 4'
        },
        {
            text: (
                <div>Carol and Jean were up at 6:30 and we all ate at 7, then got an early start on a short hike. A side note on breakfasts in the region: we rarely got more than bread or small packaged toast with butter, jam, and maybe Nutella. Occasionally there were small cakes or maybe cereal. If you are concerned about protein, bring your own protein bars! We had to adapt to large sugary breakfasts, and by day 4, I was already looking forward to eggs and peanut butter.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/Cloudy.jpg',
            detailText: 'Carol looking at the clouds where Mont Blanc is supposed to be.'
        },
        {
            text: (
                <div>Miraculously, after stretching yesterday evening and eating a large dinner, we felt great on the trail. The 460m elevation gain was done in a little over an hour, then we descended for two and half hours through Aosta Valley to Courmayeur. We arrived in time for an early lunch of pizza.</div>
            )
        },
        {
            text: (
                <div>Peter’s hat had fallen off his bag and I’d lost my headphones the previous day, so we had to spend some money to replace both, as well as buying some more very expensive protein bars. After lunch we set out to pick up a few things at the stores — then discovered that all the shops closed from 12:30 to 3. Seems nice to be Italian, if you get three hours for lunch with wine every day.</div>
            )
        },
        {
            text: (
                <div><i>Grade: A- (for the losing-things woes)</i></div>
            )
        },
        {
            header: 'Day 5'
        },
        {
            text: (
                <div>On our 5th day, the sun lit up walls of rock and ice along Mont de la Saxe – views that were a highlight of the trip. Even a late start after our morning errands with 19 km ahead of us didn’t make a dent in the day, and luckily the weather had cooled off slightly.</div>
            )
        },
        {
            text: (
                <div>The book says of this section: “the stage is a delight from start to finish” and “the visual prospects for the route […] are spectacular.” Yup, confirmed. We got a view of the range that includes Mont Blanc from the south, though it was cloudy again.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/View2.jpg'
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/selfie.jpg'
        },
        {
            text: (
                <div>We stopped for lunch at Rifugio Bonatti, where I learned that polenta can be a great main dish topped with mushrooms, cheese or sausage. In the afternoon, a crackly boom of thunder shocked us and we turned to see an ominous dark cloud advancing in our direction. That’s a pretty good motivator to keep a quick pace for the last stretch.</div>
            )
        },
        {
            text: (
                <div>At dinner we sat next to an older couple from Luxembourg. Ruth worked as a midwife and had raised five children with her husband Chierry, and we listened with interest as they talked about the differences in child-rearing in Europe. Chierry seemed to be a fairly traditional French man and was surprised that anyone we knew would choose not to have children, which he thought was selfish. I really enjoy hearing other people’s viewpoints, particularly when I disagree — those are the types of conversations we’d stopped encountering in San Francisco, where everyone was our age and had similar values and political views.</div>
            )
        },
        {
            text: (
                <div><i>Grade: A+</i></div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/Bonatti.jpg',
            detailText: 'The incredible view from Rifugio Bonatti.'
        },
        {
            header: 'Day 6'
        },
        {
            text: (
                <div>Yesterday’s thunderstorm made for a dark and windy morning. We climbed a steep ridge to the mountainous border between Italy and Switzerland, stopping frequently to add or remove our outer layers. The fog swirled and glided by along a river that flowed back down the valley. By now, our legs had gained mountain-walking muscles and it was a short hour to crest the ridge. However, we both had nagging pains — Peter’s heels and toes were raw from his boots, and my right Achilles tendon and left knee were inflamed. We stopped about an hour down the hill for me to lie down with my legs in the air, a tip I’d gotten from another hiker that really helped.</div>
            )
        },
        {
            text: (
                <div>La Fouly is essentially one main street with the Auberge des Glaciers, two hotels, and a small grocery store. At dinner we met a woman from San Francisco named Megan and discovered a mutual friend. She ordered a half liter of house wine and was shocked when the bill came to 38 Euros — we’d heard Switzerland was expensive, but still! Since the next day was a rest day, we had decided a little wine wouldn’t hurt and had a glass (of the cheapest stuff, of course), then stayed up later than normal chatting.</div>
            )
        },
        {
            text: (
                <div>Megan had come with an organized group and they were completing the TMB in 6 days by taking a few busses and walking close to 20 km every day. So far, everyone Peter and I had met were either with a guide-led group or had gone through an organization to set the schedule and book all accommodation. Self-organizing had been difficult — the TMB goes through three countries and there’s no main governing board. One site listed all the refuges with contact information, but many didn’t have online reservations and the phone numbers didn’t always go through. You’d plot out your route only to find that the lodging for half of the days was unavailable. And it might have been possible without speaking French, but much more difficult. It does cost a lot more to have someone book for you, but if you are able to pay for it I’d recommend doing that.</div>
            )
        },
        {
            text: (
                <div><i>Grade: B+</i></div>
            )
        },
        {
            header: 'Day 7'
        },
        {
            text: (
                <div>Rest day! Got a lot of reading, podcast-listening, and writing done, and paid $8 for an espresso and tiny black coffee. I also caught up on budget tracking by going through every receipt from the week, a task I’ve actually come to enjoy. I’d always been horrible at tracking my spending and budgeting it, and it’s gratifying now to see where we’re saving money.</div>
            )
        },
        {
            text: (
                <div><i>Grade: A</i></div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/Lake.jpg',
            detailText: 'The turquoise lake in Champex.'
        },
        {
            header: 'Day 8'
        },
        {
            text: (
                <div>Today’s stage was advertised as the easiest day of the TMB, a long and easy downhill following a river for most of the day with idyllic pastoral views instead of the usual sharp glacier peaks. For some reason, our rest day seemed to have thrown us into a funk and neither of us had much energy for today’s hills. It was also getting very warm and humid in the trees. I don’t do well in hot weather, and got increasingly grumpy as we approached Champex — an entirely avoidable mood swing.</div>
            )
        },
        {
            text: (
                <div>When we got to Champex we saw that our refuge, Gîte Bon Abri, was another 2 km uphill past the town. We headed there to get lunch and thought that at least we’d knock out the beginning of tomorrow’s hike, but when we got there two teenage girls (probably the owner’s daughters and not employees) were entirely unhelpful, telling us they did not serve lunch, didn’t know where we could find it except to hike back to Champex, and no, we couldn’t leave our bags there before the check-in window began at 4. In general, the gîtes weren’t as hospitable as the refuges, and I would recommend skipping them if you can find another place to stay.</div>
            )
        },
        {
            text: (
                <div>We searched for food nearby, but quickly realized we would need to walk back to Champex with our bags. I’m embarrassed to say some tears escaped. We eventually got pizza and lay in the grass by the lake for the afternoon, but it was definitely the low point of the trip.</div>
            )
        },
        {
            text: (
                <div><i>Grade: D</i></div>
            )
        },
        {
            header: 'Day 9'
        },
        {
            text: (
                <div>To avoid a repeat of midday heat, we got up at 6:30 a.m. and were out the door shortly after the sugary 7 a.m. breakfast. After an hour of gentle downhill, we climbed up to one of the most stunning views of my life. From the grassy slope just before Alp Bovine (yes, named for the cows) we had a panoramic view of blue shaded mountains rising across the Rhône valley.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/panorama.jpg'
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/Cow.jpg'
        },
        {
            text: (
                <div>On the way up, we’d been passed by an ultra trail runner from San Diego, Drew, whom we’d met the day before. He had originally planned to run the high route of the TMB, but it was closed. He had been walking the main route with the rest of us and had realized he didn’t actually like hiking that much — he wanted the speed and “fun” of running the hills. That afternoon he would take a bus to a place where he could run the rest of the trail. His opinion, 'if you aren’t having fun, stop doing it,’ is something we’ll keep in mind for future travels.</div>
            )
        },
        {
            text: (
                <div><i>Grade: A</i></div>
            )
        },
        {
            header: 'Day 10'
        },
        {
            text: (
                <div>KR’s book has several suggestions of alternative routes, usually with better views, but often longer or steeper. On day 10 we accidentally took one of them which was “in some ways, a better and more varied route than the traditional stage […] although it’s more strenuous than first appears.” Oops.</div>
            )
        },
        {
            text: (
                <div>Though the route was a bit longer than the main trail, it was a nice walk alternating through wildflower fields, wooded hillsides, and a couple of snow crossings. We reached the altitude equivalent to the day’s main peak, Col de Balme, early in the hike. We sat there eating our picnic, looking out at the Chamonix valley and an impressive mountain range, including our best view yet of Mont Blanc.</div>
            )
        },
        {
            text: (
                <div><i>Grade: A</i></div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/MontBlanc.jpg',
            detailText: 'Finally, Mont Blanc!'
        },
        {
            header: 'Day 11'
        },
        {
            text: (
                <div>Though we’d gotten stronger during the trip, our bodies were starting to give out by the last day. We started at the La Boerne refuge and had originally planned to walk the detour to Lac Blanc, an alpine lake. But the initial hill was so exhausting that we scrapped our plans and headed straight to the cable car, skipping the last major downhill into Courmayeur as well. Since all the hills are so steep, the downhill is often as difficult as going up. </div>
            )
        },
        {
            text: (
                <div>As we crested the hill and saw the ski lodge where we knew we could get lunch, I was tired but thrilled we’d finished. In total we had walked a bit less than 100 miles. Peter says he prefers 4-5 day long treks, but I’d be interested in tackling longer ones like the Camino in Spain or treks in Patagonia. I guess we’ll find out!</div>
            )
        },
        {
            text: (
                <div><i>Grade: A</i></div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/TheEnd.jpg',
            detailText: 'The end is in sight!'
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/westernEurope/tmb/Chamonix.jpg',
            detailText: 'Arriving back in Chamonix.'
        },
    ]
}


export default tmb


// WEBPACK FOOTER //
// ./src/Posts/Content/TMB.js